import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Auth0Provider } from '@auth0/auth0-react';
import createBrowserHistory from 'history/createBrowserHistory';
import { routerMiddleware, ConnectedRouter } from 'react-router-redux';
import { Route } from 'react-router-dom';
import { StaticRoutes } from './Routes';
import App from './App';
import getStore from './getStore';
import getConfig, { isLowerEnvironment } from './config';
import getClientConfig from './clientConfig';
import logMetric from './logging/logMetric';
import lookup from './api/lookupData';
import getUserProfile from './userProfile';
import './polyfill';
import { CLIENT_CENTER_CLIENT_ID, TANGO_CLIENT_ID } from './constants';
import Auth0TokenSync from './components/Authentication/Auth0TokenSync';
import ExternalTokenListener from './components/Authentication/ExternalTokenListener';

const AUTH_TOKEN_STORAGE_KEY = 'authToken';
const AUTH_TOKEN_PARAMETER_KEY = 'access_token';
const CLIENT_ID_STORAGE_KEY = 'clientId';
const CLIENT_ID_PARAMETER_KEY = 'client_id';
const USER_ID_STORAGE_KEY = 'userId';
const USER_ID_PARAMETER_KEY = 'user_id';
const ALTERNATE_USER_ID_PARAMETER_KEY = 'userid';

const getParameter = (parameterKey, storageKey) => {
  let param = null;
  const searchKey = ('[\\?&]').concat(parameterKey, '=([^&#]*)');
  const result = new RegExp(searchKey).exec(window.location.href);

  if (result) {
    param = decodeURIComponent(result[1]);
    param = param === 'undefined' || param === 'null' ? undefined : param;
  }

  const fromStorage = sessionStorage.getItem(storageKey);
  if (fromStorage === 'undefined' || fromStorage === 'null') {
    return undefined;
  }

  return param || fromStorage;
};

const getInitialState = () => {
  const referrer = document.referrer;
  const configuration = getConfig();
  const authToken = getParameter(AUTH_TOKEN_PARAMETER_KEY, AUTH_TOKEN_STORAGE_KEY);
  
  let clientId = getParameter(CLIENT_ID_PARAMETER_KEY, CLIENT_ID_STORAGE_KEY);
  if (!clientId) {
    clientId = isLowerEnvironment(configuration.env) ? TANGO_CLIENT_ID : CLIENT_CENTER_CLIENT_ID;
  }

  const clientConfig = getClientConfig(clientId);
  const userProfile = getUserProfile();
  let userId = getParameter(USER_ID_PARAMETER_KEY)
    || getParameter(ALTERNATE_USER_ID_PARAMETER_KEY, USER_ID_STORAGE_KEY)
    || 'unspecified';

  if (authToken === '1234') userId = 'omgjsam';

  logMetric('initialized', { clientId, referrer });

  sessionStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
  sessionStorage.setItem(USER_ID_STORAGE_KEY, userId);
  if (authToken) {
    sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, authToken);
  } else {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  }

  const authentication = { authToken, isAuthenticated: authToken !== 'null' && !!authToken };

  return { configuration, authentication, userId, clientId, lookup, clientConfig, userProfile };
};

const history = createBrowserHistory();

const initialState = getInitialState();
const store = getStore(initialState, [routerMiddleware(history)]);

const appTree = (
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <React.Fragment>
        <ExternalTokenListener />
        <Auth0TokenSync />
        <Route path={StaticRoutes.ROOT} component={App} />
      </React.Fragment>
    </ConnectedRouter>
  </Provider>
);

const getAuth0AuthorizationParams = configuration => {
  const params = {
    redirect_uri: `${window.location.origin}${window.location.pathname}`,
    scope: configuration.authScope || 'openid profile',
  };

  if (configuration.authAudience) {
    params.audience = configuration.authAudience;
  }

  return params;
};

const renderApp = () => {
  const { configuration } = initialState;
  const auth0Enabled = !!(configuration.auth0Domain && configuration.auth0ClientId);

  const rootNode = auth0Enabled ? (
    <Auth0Provider
      domain={configuration.auth0Domain}
      clientId={configuration.auth0ClientId}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      authorizationParams={getAuth0AuthorizationParams(configuration)}
      onRedirectCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {appTree}
    </Auth0Provider>
  ) : appTree;

  ReactDOM.render(
    rootNode,
    document.getElementById('root')
  );
};

history.listen(location => { window.parent.postMessage(location, '*'); });

renderApp();
